import { type PointerEvent, type PointerEventHandler, useState } from 'react'

import { AGENT_FRAME_SELECTOR } from '../components/widget/agentFrameMarker'
import type { AgentPanelSize } from '../components/widget/agentWidgetLayout'
import { type PanelResizeEdge, resizedPanelSize, savePanelSize } from '../components/widget/panelSize'

// ドラッグの起点（押したつまみ、押した時点の枠の大きさとポインターの位置）。押したときに入れ、離したときに消す。
// 動かしている間の大きさは持たず、そのたびに起点とポインターの位置から計算する。
// 描き直しの前に次のイベントが届くと、つまみの処理は前の描画の値を読むが、起点は押してから変わらないので、どのイベントでも同じ値を読める
interface DragStart {
  edge: PanelResizeEdge
  pointerId: number
  start: AgentPanelSize
  startPointer: { x: number; y: number }
}

// つまみの要素に付けるポインターの処理
type HandleHandlers = Record<
  'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel',
  PointerEventHandler<HTMLDivElement>
>

const sizeAt = (drag: DragStart, event: PointerEvent<HTMLDivElement>): AgentPanelSize =>
  resizedPanelSize({
    edge: drag.edge,
    start: drag.start,
    startPointer: drag.startPointer,
    pointer: { x: event.clientX, y: event.clientY },
    viewport: { width: window.innerWidth, height: window.innerHeight },
  })

/**
 * パネルの辺や角のつまみに付ける、ドラッグで大きさを変える処理を返す。
 * つまみを押したら、そのときの枠の実際の大きさ（既定の高さは vh で決まるので、DOM から読む）とポインターの位置を覚え、
 * 動かすたびに大きさを onResize に渡して描き直し、離したら、離した位置から大きさを計算し直して localStorage に覚える。
 * 押した位置から動かさずに離しただけなら覚えない。既定の高さ（vh）が、押した時点の px の固定値に変わってしまうためである。
 * 途中で取り消されたとき（pointercancel）も覚えない。そのときのポインターの位置は、離した位置として当てにならないためである。
 * ポインターキャプチャ（既存の ClosableResizableSider と同じ）で、つまみの外へ出ても動きを追う
 */
export const usePanelResize = (
  onResize: (size: AgentPanelSize) => void,
): ((edge: PanelResizeEdge) => HandleHandlers) => {
  const [dragStart, setDragStart] = useState<DragStart | null>(null)

  // ドラッグを終える。save が true で、押した位置から動いていれば、離した位置の大きさを覚える
  const finish = (event: PointerEvent<HTMLDivElement>, save: boolean): void => {
    if (dragStart?.pointerId !== event.pointerId) {
      return
    }
    setDragStart(null)
    event.currentTarget.releasePointerCapture(event.pointerId)
    const moved = event.clientX !== dragStart.startPointer.x || event.clientY !== dragStart.startPointer.y
    if (!save || !moved) {
      return
    }
    const size = sizeAt(dragStart, event)
    onResize(size)
    savePanelSize(size)
  }

  return (edge) => ({
    onPointerDown: (event): void => {
      const frame = event.currentTarget.closest(AGENT_FRAME_SELECTOR)
      if (frame === null || dragStart !== null) {
        return
      }
      // 押したまま動かしても、下の文字を選択しない
      event.preventDefault()
      const { width, height } = frame.getBoundingClientRect()
      setDragStart({
        edge,
        pointerId: event.pointerId,
        start: { width, height },
        startPointer: { x: event.clientX, y: event.clientY },
      })
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    onPointerMove: (event): void => {
      if (dragStart?.pointerId !== event.pointerId) {
        return
      }
      onResize(sizeAt(dragStart, event))
    },
    onPointerUp: (event): void => {
      finish(event, true)
    },
    onPointerCancel: (event): void => {
      finish(event, false)
    },
  })
}
